import { PageSEO } from './types';

export const COMPANY_NAME = 'BizSolutions.Tech';

/**
 * Canonical origin. `www` is the canonical host -- every absolute URL the site
 * emits (canonical, og:url, sitemap, form redirects) must agree on it, or the
 * non-www host bounces through a redirect.
 */
export const SITE_URL = 'https://www.bizsolutions.tech';

/** Default social card image. Lives in public/, so it ships un-hashed. */
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

export const NAP = {
  email: 'info@bizsolutions.tech',
  phone: {
    display: '+1 (858) 358-6833',
    href: 'tel:+18583586833',
  },
  address: {
    full: '1 Creative Place Charleston, WV 25311',
  },
  hours: {
    lines: ['Mon-Fri: 9:00 AM - 6:00 PM', 'Sat: 10:00 AM - 2:00 PM'],
    timezone: 'Eastern Time',
  },
};

/**
 * The site's top-level destinations, in nav order.
 *
 * The header nav and the footer both have to reach every one of these, and
 * `Footer.test.tsx` asserts it against this list. Without something declaring
 * the set, the two drifted: the footer was missing Events and Careers
 * entirely, and had no Home link, while the header had all three.
 *
 * The header keeps its own `navItems` because each entry carries a dropdown
 * panel this list has no business describing. What the test holds is that the
 * two agree on where the site can go.
 */
export const PRIMARY_NAV: ReadonlyArray<{ label: string; path: string }> = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'Insights', path: '/insights' },
  { label: 'Events', path: '/events' },
  { label: 'About', path: '/about' },
  { label: 'Careers', path: '/careers' },
  { label: 'Contact', path: '/contact' },
];

export const SOCIAL_LINKS = {
  facebook: 'https://www.facebook.com/bizsolutions.tech',
  linkedin: 'https://www.linkedin.com/company/business-solutions-and-technologies-llc',
  youtube: 'https://www.youtube.com/channel/UCkxx3yHym7_c_3e7m05RHHA',
};

/**
 * Per-page SEO copy, consumed by <Seo> on every route.
 *
 * Service detail pages are deliberately absent: /services/:slug generates its
 * title and description from `content/services.ts` so a new service needs one
 * edit, not two. Insight detail pages (/insights/:slug) build theirs at
 * runtime from the API response.
 *
 * `path` is also the sitemap's source of truth -- see scripts/routes.js.
 */
export const PAGE_SEO: Record<string, PageSEO> = {
  home: {
    path: '/',
    title: 'BizSolutions.Tech | ServiceNow & IT Consulting',
    description:
      'BizSolutions.Tech is an IT consulting firm providing ServiceNow implementation, AI, and custom software development services.',
  },
  services: {
    path: '/services',
    // Practice hubs and service pages derive their own metadata from
    // content/services.ts. This entry covers the overview only, and names
    // services that exist -- "platform automation" was a service that the
    // taxonomy rebuild deleted.
    title: 'ServiceNow & Cloud Services | BizSolutions.Tech',
    description:
      'ServiceNow implementation, CMDB, integrations, and custom applications, plus AWS architecture and AI-assisted workflows.',
  },
  about: {
    path: '/about',
    title: 'About Us | BizSolutions.Tech',
    description:
      'Learn about BizSolutions.Tech’s mission, values, leadership team, and the communities we support.',
  },
  contact: {
    path: '/contact',
    title: 'Contact Us | BizSolutions.Tech',
    description: 'Get in touch with BizSolutions.Tech to discuss your ServiceNow or custom development project.',
  },
  careers: {
    path: '/careers',
    title: 'Careers | BizSolutions.Tech',
    description: 'Explore open positions and what it’s like to work at BizSolutions.Tech.',
  },
  insights: {
    path: '/insights',
    title: 'Insights | BizSolutions.Tech',
    description:
      'Articles from the BizSolutions.Tech team on ServiceNow, AI, and digital transformation.',
  },
  events: {
    path: '/events',
    title: 'Events | BizSolutions.Tech',
    description: 'Upcoming and past events, meetups, and conferences BizSolutions.Tech takes part in.',
  },
  privacyPolicy: {
    path: '/privacy-policy',
    title: 'Privacy Policy | BizSolutions.Tech',
    description: 'How BizSolutions.Tech collects, uses, and protects the information you share with us.',
  },
  notFound: {
    path: '/404',
    title: 'Page Not Found | BizSolutions.Tech',
    description: 'The page you were looking for does not exist.',
    noIndex: true,
  },
  unauthorized: {
    path: '/unauthorized',
    title: 'Unauthorized | BizSolutions.Tech',
    description: 'You do not have access to this page.',
    noIndex: true,
  },
  admin: {
    path: '/admin',
    title: 'Admin | BizSolutions.Tech',
    description: 'BizSolutions.Tech content administration.',
    noIndex: true,
  },
};
